import { defineComponent } from 'vue'

import './audit-campaign-pop-up.scss'

import { from, of } from 'rxjs'
import { catchError, concatMap, mergeMap, pluck, retryWhen, tap } from 'rxjs/operators'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import { SearchParams } from '@skinsoft/types'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { genericRetryStrategy } from '@skinsoft/rxjs-utils'
import { NB_CAMPAIGNS_SEARCH_RESULT_CHANGED_TOPIC } from '../../../../busTopics'
import { AuditCampaignFeaturedList } from '../../audit-campaign-search/audit-campaign-featured-list'
import { NewAuditRecordNotFoundError } from './errors'

const NB_TRY_FIND_NEW_RECORD = 15
const DELAY_TRY_FIND_NEW_RECORD = 1000

export const AuditCampaignPopUp = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        auditCampaignFeaturedList: AuditCampaignFeaturedList
    },
    props: ['linkableItemId', 'linkableItemType'],
    template: require('./audit-campaign-pop-up.html'),
    data() {
        return {
            loadingIcon: faCircleNotch,
            loading: false,
            campaignCount: 0,
            pageNumber: 1,
            currentPage: 1,
            searchParams: {
                nbResults: 5,
                queryText: '',
                sortAsc: true,
                sortType: ['5e2713835111f87e825edddc'],
                startAt: 0
            } as SearchParams,
        }
    },
    mounted() {
        this.open()
        this.subscribeToBus(NB_CAMPAIGNS_SEARCH_RESULT_CHANGED_TOPIC, res => {
            this.campaignCount = res.payload.totalResults
            this.pageNumber = Math.ceil(this.campaignCount / this.searchParams.nbResults)
            this.currentPage = this.searchParams.startAt! + 1
            this.unsubscribeFrom(NB_CAMPAIGNS_SEARCH_RESULT_CHANGED_TOPIC)
        })
    },
    computed: {
        canMoveBackward(): boolean {
            return this.searchParams.startAt! > 0
        },

        canMoveForward(): boolean {
            return this.searchParams.startAt! + 1 < Math.floor(this.campaignCount / this.searchParams.nbResults) + (this.campaignCount % this.searchParams.nbResults ? 1 : 0)
        },
    },
    methods: {
        open() {
            (this.$refs.modal as any).show()
        },

        addToCampaign(auditCampaignId: string) {
            this.loading = true
            this.$auditCampaignDataStore.fetchOne(auditCampaignId).pipe(
                mergeMap(auditCampaign => {
                    return from(this.$auditCampaignDataStore.addItem(
                        auditCampaign,
                        this.linkableItemId,
                        this.linkableItemType
                    ))
                }),
                concatMap(value => {
                    return of(value).pipe(
                        mergeMap(() => this.$auditCampaignDataStore.fetchOne(auditCampaignId)),
                        pluck('auditProjectSchema', 'auditReports'),
                        mergeMap(allReports => {
                            return this.$recordDataStore.searchAll({
                                pageSize: 1,
                                types: ['AuditRecord', 'Recolement'],
                                page: 0,
                                advancedSearches: [{
                                    leftCriteria: {
                                        entityField: '5d5d3797bbffbb20bbcca5fa',// linked item
                                        operator: 'equals',
                                        type: 'criteria',
                                        value: {
                                            completeSearch: false,
                                            refId: this.linkableItemId,
                                            type: 'ref'
                                        }
                                    },
                                    linkOperator: null,
                                    rightCriteria: null,
                                    type: 'requestSearch'
                                }],
                                // @ts-ignore
                                filteredIds: allReports,
                            })
                        }),
                        tap(res => {
                            // @ts-ignore
                            if (res.totalSize === 0) {
                                throw new NewAuditRecordNotFoundError()
                            }
                        }),
                        catchError(() => this.$auditCampaignDataStore.fetchOne(auditCampaignId).pipe(
                            pluck('auditProjectSchema', 'auditReports'),
                            mergeMap(allReports => {
                                    return this.$recordDataStore.searchAllOffline({
                                        types: ['AuditRecord', 'Recolement'],
                                        page: 0,
                                        pageSize: 1,
                                        // @ts-ignore
                                        filteredIds: allReports,
                                        advancedSearches: [{
                                            leftCriteria: {
                                                entityField: '5d5d3797bbffbb20bbcca5fa',// linked item
                                                operator: 'equals',
                                                type: 'criteria',
                                                value: {
                                                    completeSearch: false,
                                                    refId: this.linkableItemId,
                                                    type: 'ref'
                                                }
                                            },
                                            linkOperator: null,
                                            rightCriteria: null,
                                            type: 'requestSearch'
                                        }],
                                    })
                                })
                        )),
                        tap(res => {
                            // @ts-ignore
                            if (res.totalSize === 0) {
                                throw new NewAuditRecordNotFoundError()
                            }
                        }),
                        retryWhen(genericRetryStrategy({
                            maxRetryAttempts: NB_TRY_FIND_NEW_RECORD,
                            scalingDuration: [DELAY_TRY_FIND_NEW_RECORD],
                        })),
                        pluck('allData', '0', 'id')
                    )
                })
            ).subscribe(
                target => {
                    this.$emit('close')
                    this.$router.push(`/record/${auditCampaignId}/${target}`)
                },
                () => {
                    this.loading = false
                }
            )
        },

        changePage(forward: boolean) {
            if (forward) {
                if (this.canMoveForward) {
                    ++this.searchParams.startAt!
                }
            } else if (this.canMoveBackward) {
                --this.searchParams.startAt!
            }
        },
    },
})
